import type { ConnectedTypes } from '../../../make-connected';
import type { Types } from '../../../types';
import type { Collection } from '../../../../core/types/common';
import { getVertexId } from '../../../../core/util';
import type { GroupWithExclude } from '../../../../core/interfaces/vertices/util/edges';
import type { Interval } from '../../../../core/interfaces';

export function min2hrs (min: number | undefined | null) {
  if (min == null) return;
  return min / 60;
}

/**
 * Converts a time string in the format "HH:MM" to a float representation, e.g., "12:30" becomes 12.3.
 */
export function toTimeFloat (str: string) {
  return parseFloat(str.replace(':', '.'));
}

export function getPeriodIndex (
  period:     ConnectedTypes.period | string | undefined | null,
  periodsMap: Map<string | undefined, number>,
  options:    Types.parsedToOptions
): number | undefined {
  // if no periods exist, return undefined
  if (periodsMap.size == 0) return;

  // if no period is given, return undefined
  if (!period) return;

  const id = getVertexId(period, options);
  const periodIndex = periodsMap.get(id);
  if (periodIndex === undefined) {
    throw new Error(`(RS::To::getPeriodIndex) Period "${id}" is not in periodsMap`);
  }
  return periodIndex;
}

export const COLLECTION_ID = {
  persons:     'persons'     as const,
  groups:      'groups'      as const,
  teachers:    'teachers'    as const,
  events:      'events'      as const,
  lockedTimes: 'lockedtimes' as const,
} satisfies Partial<Record<Collection, string>>;

/**
 * a type generic that inverses a record type, i.e., { a: 'x', b: 'y' } becomes { x: 'a', y: 'b' }
 */
type InverseRecord<T extends Record<string, string>> = {
  [K in keyof T as T[K]]: K
};

const COLLECTION_BY_ID = Object.fromEntries(Object.entries(COLLECTION_ID).map(([collection, id]) => [id, collection])) as InverseRecord<typeof COLLECTION_ID>;


export type AggregateCollection = keyof typeof COLLECTION_ID;

type AggregateIdParts<TCollection extends AggregateCollection = AggregateCollection> = {
  collection: TCollection
  entityId:   string
};


function parseAggregateId<TCollection extends AggregateCollection = AggregateCollection> (
  aggregateId: string,
  expectedCollection?: TCollection
): AggregateIdParts<TCollection> {
  const indexOfDot = aggregateId.indexOf('.');
  if (indexOfDot == -1) {
    throw new Error(`(RS::To::fromId) Missing collection separator in aggregate id "${aggregateId}"`);
  }

  const collectionId = aggregateId.substring(0, indexOfDot).toLowerCase() as keyof typeof COLLECTION_BY_ID;
  const entityId = aggregateId.substring(indexOfDot + 1);

  if (!entityId) {
    throw new Error(`(RS::To::fromId) Missing entity id in aggregate id "${aggregateId}"`);
  }

  const collection = COLLECTION_BY_ID[collectionId];
  if (!collection) {
    throw new Error(`(RS::To::fromId) Unknown collection id "${collectionId}" in aggregate id "${aggregateId}"`);
  }

  if (expectedCollection && collection != expectedCollection) {
    throw new Error(`(RS::To::fromId) Expected collection "${expectedCollection}" but got "${collection}" in aggregate id "${aggregateId}"`);
  }

  return {
    collection: collection as TCollection,
    entityId,
  };
}

/**
 * returns a combined id for the vertex or edge by combining the type(s) and id(s).
 */
export namespace idOf {
  /** `persons.id` */
  export function person (
    person:  ConnectedTypes.person | string,
    options: Types.parsedToOptions
  ): string {
    return `${COLLECTION_ID.persons}.${ getVertexId(person, options) }`;
  }

  /** `groups.id` */
  export function group (
    group:   ConnectedTypes.group | string,
    options: Types.parsedToOptions
  ): string {
    return `${COLLECTION_ID.groups}.${ getVertexId(group, options) }`;
  }

  /** `teachers.id` */
  export function teacher (
    teacher: ConnectedTypes.teacher | string,
    options: Types.parsedToOptions
  ): string {
    return `${COLLECTION_ID.teachers}.${ getVertexId(teacher, options) }`;
  }

  /** `events.id` */
  export function event (
    event:   ConnectedTypes.event | string,
    options: Types.parsedToOptions
  ): string {
    return `${COLLECTION_ID.events}.${ getVertexId(event, options) }`;
  }

  /** `lockedtimes.id` */
  export function lockedTime (
    lockedTime: ConnectedTypes.lockedTime | string,
    options:    Types.parsedToOptions
  ): string {
    return `${COLLECTION_ID.lockedTimes}.${ getVertexId(lockedTime, options) }`;
  }

  /** `groups.id<.exclude.id1.id2...>` */
  export function groupReference (
    group:   GroupWithExclude.Util.Value<ConnectedTypes.group, ConnectedTypes.person>,
    options: Types.parsedToOptions
  ): string {
    return `${COLLECTION_ID.groups}.${ getVertexId(group.to, options) }`
      + (group.exclude?.length
        ? '.exclude.' + group.exclude
          .map(x => getVertexId(x, options))
          .sort()
          .join('.')
        : '');
  }

  /** `<rootIntervalsId&>JSON.stringify(intervals)>` */
  export function intervalPairReference (
    interval:     Interval[]                  | undefined,
    rootInterval: ConnectedTypes.rootInterval | undefined | null,
    options:      Types.parsedToOptions
  ): string {
    // replace null with undefined
    if (interval     === null) interval     = undefined;
    if (rootInterval === null) rootInterval = undefined;

    // replace empty intervals with undefined
    if (interval && interval.length == 0) interval = undefined;

    const out = (rootInterval ? getVertexId(rootInterval, options) + '&' : '') + JSON.stringify(interval);
    return out;
  }
}

export namespace fromId {
  export function aggregate (aggregateId: string): AggregateIdParts {
    return parseAggregateId(aggregateId);
  }

  /** `persons.id` -> `id` */
  export function person (aggregateId: string): string {
    return parseAggregateId(aggregateId, 'persons').entityId;
  }

  /** `groups.id` -> `id` */
  export function group (aggregateId: string): string {
    return parseAggregateId(aggregateId, 'groups').entityId;
  }

  /** `teachers.id` -> `id` */
  export function teacher (aggregateId: string): string {
    return parseAggregateId(aggregateId, 'teachers').entityId;
  }

  /** `events.id` -> `id` */
  export function event (aggregateId: string): string {
    return parseAggregateId(aggregateId, 'events').entityId;
  }

  /** `lockedtimes.id` -> `id` */
  export function lockedTime (aggregateId: string): string {
    return parseAggregateId(aggregateId, 'lockedTimes').entityId;
  }
}
